import type { Session } from "@supabase/supabase-js";
import { supa } from "./supa";

/**
 * Passwordless email one-time code. Deliberately not magic links: no redirect
 * URL allowlisting, no deep-link plumbing, and identical behaviour on the web
 * and inside a Capacitor shell if this ever ships as a native app.
 *
 * NOTE: this only works if the Supabase "Magic Link" email template was edited
 * to send `{{ .Token }}`. Left at its default it emails a LINK instead, and this
 * flow silently has no code to enter. See SETUP.md.
 */

/** Email a sign-in code. Creates the account on first use — this IS registration. */
export function sendEmailCode(email: string) {
  return supa.auth.signInWithOtp({
    email: email.trim(),
    options: { shouldCreateUser: true },
  });
}

/** Exchange the emailed code for a session. */
export function verifyEmailCode(email: string, token: string) {
  return supa.auth.verifyOtp({
    email: email.trim(),
    token: token.replace(/\s/g, ""), // users paste "123 456" from the email
    type: "email",
  });
}

export function signOut() {
  return supa.auth.signOut();
}

export async function currentSession(): Promise<Session | null> {
  const { data } = await supa.auth.getSession();
  return data.session;
}

export async function currentUserId(): Promise<string | null> {
  const { data } = await supa.auth.getSession();
  return data.session?.user.id ?? null;
}

/**
 * The single hub every auth-dependent behaviour hangs off.
 *
 * `cb` fires once immediately with event "INITIAL_SESSION" (session may be null),
 * then on every subsequent change. Callers distinguish a fresh login from a
 * restored session by tracking the previous user id themselves — see main.ts.
 */
export function onAuthChange(cb: (session: Session | null, event: string) => void) {
  const { data } = supa.auth.onAuthStateChange((event, session) => cb(session, event));
  return () => data.subscription.unsubscribe();
}
